import React from 'react';

import { DeprecatedInterpolatedString } from '~commonInfra/components/InterpolatedString';
import { BrowserTitleService } from '~commonInfra/services/BrowserTitleService';
import { ServerUrls } from '~commonInfra/url/ServerUrls';
import { SiteSwitchErrorCodes, SolrErrorCode } from '~commonInfra/utils/ErrorCodes';
import { errorPageDivId } from '~src/spacesPlatform/components/layout/SkipBarProvider';
import { SpacesSiteUrls } from '~src/spacesPlatform/components/spaceComponents/SpacesSiteUrls';

import { LoginUrlsWrapper } from '../../authentication/components/loginSpace/LoginUrls';
import { Messages } from '../../codegen/Localize';
import { getSessionManagementServiceInstance, SessionManagementService } from '../../sessionManagement/SessionManagementService';
import { IUserInfo } from '../../userManagement/users/models/IUserInfo';
import { RouterErrorType } from '../redux/state/RouterErrorType';

export interface DataAlertErrorData {
  readonly viewUrl: string;
  readonly owner: IUserInfo;
}

export interface RouterErrorOwnProps {
  readonly errorType: RouterErrorType;
  readonly urls: SpacesSiteUrls;
  readonly code?: string;
  readonly dataAlertErrorData?: DataAlertErrorData;
}

export interface RouterErrorServicesProps {
  readonly server: SessionManagementService;
}

export type RouterErrorProps = RouterErrorServicesProps & RouterErrorOwnProps;

// This class is meant to mimic the logic and UI in ErrorCtrl and Error.pug
export class RouterErrorComponent extends React.PureComponent<RouterErrorProps> {
  public componentDidMount() {
    BrowserTitleService.setTitleToServerName();
  }

  public render(): JSX.Element {
    const errorState = getErrorState(this.props.errorType, this.props.urls, this.props.code, this.props.server);
    return (
     <div style={{height: 'calc(100vh - 15%)', flex: '1', position: 'relative', outline: 'none'}} id={errorPageDivId} tabIndex={-1}>
       <div className="tb-error-box">
        <div role="alert" aria-atomic="true">
          {this.getErrorTextElements(errorState)}
        </div>
        {errorState.url &&
          <a className="tb-empty-state-button" href={errorState.url} data-tb-test-id="button-back-to-content">
            <span>
              {errorState.urlMessage}
            </span>
          </a>
        }
       </div>
     </div>
    );
  }

  private getErrorTextElements(errorState: ErrorState) {
    const displayInterpolatedStringLinks = shouldUseInterpolatedStringLinks(this.props.errorType);
    if (displayInterpolatedStringLinks) {
      return(
        <div className="tb-error-box-message">
          { getInterpolatedString(this.props.errorType, this.props.dataAlertErrorData, this.props.urls)}
        </div>
      );
    } else {
      return (
        <>
          <div className="tb-error-box-message">{errorState.firstMessage}</div>
          <div className="tb-error-box-message">{errorState.secondMessage}</div>
        </>
      );
    }
  }
}

export function RouterError(props: RouterErrorOwnProps) {
  const server = getSessionManagementServiceInstance();
  return <RouterErrorComponent
    {...props}
    server={server}
  />;
}

interface ErrorState {
  readonly firstMessage: string;
  readonly secondMessage: string;
  readonly urlMessage: string;
  readonly url: string;
}

function getErrorState(errorType: RouterErrorType, urls: SpacesSiteUrls, errorCode: string, server: SessionManagementService): ErrorState {
  switch (errorType) {
    case 'siteuser':
      return {
        firstMessage: Messages.pageCouldNotBeAccessed(),
        secondMessage: Messages.userCannotBeAccessed(),
        urlMessage: Messages.backToUsers(),
        url: urls.usersUrl()
      };
    case 'serveruser':
      return {
        firstMessage: Messages.pageCouldNotBeAccessed(),
        secondMessage: Messages.userCannotBeAccessed(),
        urlMessage: Messages.backToUsers(),
        url: ServerUrls.getInstance().usersUrl()
      };
    case 'group':
      return {
        firstMessage: Messages.pageCouldNotBeAccessed(),
        secondMessage: Messages.groupCannotBeAccessed(),
        urlMessage: Messages.backToGroups(),
        url: urls.groupsUrl()
      };
    case 'groupset':
      return {
        firstMessage: Messages.pageCouldNotBeAccessed(),
        secondMessage: Messages.groupSetCannotBeAccessed(),
        urlMessage: Messages.backToGroupSets(),
        url: urls.groupSetsUrl()
      };
    case 'workbooks':
      return {
        firstMessage: Messages.pageCouldNotBeAccessed(),
        secondMessage: Messages.workbookCannotBeAccessed(),
        urlMessage: Messages.backToWorkbooks(),
        url: urls.workbooksUrl()
      };
    case 'workbook-removed-favorite':
      return {
        firstMessage: Messages.pageCouldNotBeAccessed(),
        secondMessage: Messages.favoriteWorkbookCouldNotBeFound(),
        urlMessage: Messages.backToWorkbooks(),
        url: urls.workbooksUrl()
      };

    case 'datasources':
      return {
        firstMessage: Messages.pageCouldNotBeAccessed(),
        secondMessage: Messages.datasourceCannotBeAccessed(),
        urlMessage: Messages.backToDatasources(),
        url: urls.datasourcesUrl()
      };
    case 'datasource-removed-favorite':
        return {
          firstMessage: Messages.pageCouldNotBeAccessed(),
          secondMessage: Messages.favoriteDatasourceCouldNotBeFound(),
          urlMessage: Messages.backToDatasources(),
          url: urls.datasourcesUrl()
        };

    case 'dataroles':
      return {
        firstMessage: Messages.pageCouldNotBeAccessed(),
        secondMessage: Messages.dataRoleCannotBeAccessed(),
        urlMessage: Messages.backToDataRoles(),
        url: urls.dataRolesUrl()
      };

    case 'datarole-removed-favorite':
      return {
        firstMessage: Messages.pageCouldNotBeAccessed(),
        secondMessage: Messages.favoriteDataRoleCouldNotBeFound(),
        urlMessage: Messages.backToDataRoles(),
        url: urls.dataRolesUrl()
      };

    case 'lenses':
      return {
        firstMessage: Messages.pageCouldNotBeAccessed(),
        secondMessage: Messages.lensCannotBeAccessed(),
        urlMessage: Messages.backToLenses(),
        url: urls.lensesUrl(),
      };

    case 'projects':
      return {
        firstMessage: Messages.pageCouldNotBeAccessed(),
        secondMessage: Messages.projectCannotBeAccessed(),
        urlMessage: Messages.backToProjects(),
        url: urls.projectsUrl()
      };
    case 'project-removed-favorite':
        return {
          firstMessage: Messages.pageCouldNotBeAccessed(),
          secondMessage: Messages.favoriteProjectCouldNotBeFound(),
          urlMessage: Messages.backToProjects(),
          url: urls.projectsUrl()
        };

    case 'view':
      return {
        firstMessage: Messages.pageCouldNotBeAccessed(),
        secondMessage: Messages.viewCannotBeAccessed(),
        urlMessage: Messages.backToViews(),
        url: urls.viewsUrl()
      };
    case 'view-removed-favorite':
      return {
        firstMessage: Messages.pageCouldNotBeAccessed(),
        secondMessage: Messages.favoriteViewCouldNotBeFound(),
        urlMessage: Messages.backToViews(),
        url: urls.viewsUrl()
      };
    case 'custom-view':
      return {
        firstMessage: Messages.pageCouldNotBeAccessed(),
        secondMessage: Messages.customViewCannotBeAccessed(),
        urlMessage: Messages.backToViews(),
        url: urls.viewsUrl()
      };

    case 'no-site-multi-site-user':
      return {
        firstMessage: Messages.pageCouldNotBeAccessed(),
        secondMessage: getSwitchSiteErrorSecondMessage(+errorCode),
        urlMessage: Messages.goToSiteList(),
        url: LoginUrlsWrapper.getInstance().siteUrl({})
      };

    case 'no-site-single-site-user':
      return {
        firstMessage: Messages.pageCouldNotBeAccessed(),
        secondMessage: getSwitchSiteErrorSecondMessage(+errorCode),
        urlMessage: Messages.backToContent(),
        url: urls.exploreUrl()
      };

    case 'code':
      return getErrorStateForCode(errorCode);

    case 'server-default':
      return {
        firstMessage: Messages.pageCouldNotBeAccessed(),
        secondMessage: Messages.pageDoesNotExist(),
        urlMessage: Messages.backToContent(),
        url: ServerUrls.getInstance().statusUrl()
      };

    case 'site-schedule':
      return {
        firstMessage: Messages.pageCouldNotBeAccessed(),
        secondMessage: Messages.scheduleCannotBeAccessed(),
        urlMessage: Messages.backToSchedules(),
        url: urls.schedulesUrl()
      };

    case 'server-schedule':
      return {
        firstMessage: Messages.pageCouldNotBeAccessed(),
        secondMessage: Messages.scheduleCannotBeAccessed(),
        urlMessage: Messages.backToSchedules(),
        url: ServerUrls.getInstance().schedulesUrl()
      };

    case 'collection':
      return {
        firstMessage: Messages.pageCouldNotBeAccessed(),
        secondMessage: Messages.collectionCannotBeAccessed(),
        urlMessage: Messages.backToAllCollections(),
        url: urls.collectionsUrl()
      };

    case 'metricDetail':
      return {
        firstMessage: Messages.pageCouldNotBeAccessed(),
        secondMessage: Messages.metricCannotBeAccessed(),
        urlMessage: Messages.backToAllMetrics(),
        url: urls.metricsUrl()
      };

    case 'metric-removed-favorite':
      return {
        firstMessage: Messages.pageCouldNotBeAccessed(),
        secondMessage: Messages.favoriteMetricCouldNotBeFound(),
        urlMessage: Messages.backToAllMetrics(),
        url: urls.metricsUrl()
      };

    case 'flow':
      return {
        firstMessage: Messages.pageCouldNotBeAccessed(),
        secondMessage: Messages.flowCannotBeAccessed(),
        urlMessage: Messages.backToFlows(),
        url: urls.flowsUrl(),
      };
    case 'flow-removed-favorite':
      return {
        firstMessage: Messages.pageCouldNotBeAccessed(),
        secondMessage: Messages.favoriteFlowCouldNotBeFound(),
        urlMessage: Messages.backToFlows(),
        url: urls.flowsUrl()
      };

    case 'virtualConnection':
      return {
        firstMessage: Messages.pageCouldNotBeAccessed(),
        secondMessage: Messages.publishedConnectionCannotBeAccessed(),
        urlMessage: Messages.backToPublishedConnections(),
        url: urls.virtualConnectionsUrl(),
      };
    case 'virtualConnection-removed-favorite':
      return {
        firstMessage: Messages.pageCouldNotBeAccessed(),
        secondMessage: Messages.favoritePublishedConnectionCouldNotBeFound(),
        urlMessage: Messages.backToPublishedConnections(),
        url: urls.virtualConnectionsUrl()
      };

    case 'virtualConnectionTable':
      return {
        firstMessage: Messages.pageCouldNotBeAccessed(),
        secondMessage: Messages.publishedConnectionCannotBeAccessed(),
        urlMessage: Messages.backToPublishedConnections(),
        url: urls.virtualConnectionTablesUrl(),
      };
    case 'virtualConnectionTable-removed-favorite':
      return {
        firstMessage: Messages.pageCouldNotBeAccessed(),
        secondMessage: Messages.favoritePublishedConnectionCouldNotBeFound(),
        urlMessage: Messages.backToPublishedConnections(),
        url: urls.virtualConnectionTablesUrl()
      };

    case 'insufficient-permissions':
      return {
        firstMessage: Messages.pageCouldNotBeAccessed(),
        secondMessage: Messages.permissionsPanel_insufficientPermissions(),
        urlMessage: Messages.backToContent(),
        url: urls.exploreUrl()
      };

    case 'data-alerts-disabled':
      return {
        firstMessage: Messages.pageCouldNotBeAccessed(),
        secondMessage: Messages.dataAlertsNotEnabledMessage(),
        urlMessage: Messages.backToContent(),
        url: urls.exploreUrl()
      };

    case 'view-data-alert-permissions':
    case 'view-data-alert-exists':
    case 'view-data-alert-viewer':
      const currentUser = server.sessionUserInfo();
      return {
        firstMessage: '',
        secondMessage: '',
        urlMessage: Messages.goToMyAlerts(),
        url: urls.userDataAlertsUrl(currentUser.username, currentUser.domainName)
      };

    case 'starters':
      return {
        firstMessage: Messages.pageCouldNotBeAccessed(),
        secondMessage: Messages.acceleratorsCannotBeAccessed(),
        urlMessage: Messages.backToAccelerators(),
        url: urls.startersUrl()
      };

    case 'default':
    default:
      return {
        firstMessage: Messages.pageCouldNotBeAccessed(),
        secondMessage: Messages.pageDoesNotExist(),
        urlMessage: Messages.backToContent(),
        url: urls.exploreUrl()
      };
  }
}

function getErrorStateForCode(errorCode: string): ErrorState {
  switch (+errorCode) {
    case SolrErrorCode.SolrDisabled:
    case SolrErrorCode.SolrCommunicationError:
      return {
        firstMessage: Messages.pageCouldNotBeAccessed(),
        secondMessage: Messages.serverDown(),
        urlMessage: undefined,
        url: undefined
      };

    default:
      return {
        firstMessage: Messages.pageCouldNotBeAccessed(),
        secondMessage: Messages.pageDoesNotExist(),
        urlMessage: undefined,
        url: undefined
      };
  }
}

function getSwitchSiteErrorSecondMessage(error: number): string {
  switch (error) {
    case SiteSwitchErrorCodes.UserNotMemberOfSiteOrSiteNotExist:
      return Messages.switchSiteFailedNotMemberOrNotExist();
    case SiteSwitchErrorCodes.SiteLocked:
      return Messages.switchSiteFailedSiteLocked();
    case SiteSwitchErrorCodes.SiteSuspended:
      return Messages.switchSiteFailedSiteSuspended();
    case SiteSwitchErrorCodes.UserUnlicensedForSite:
      return Messages.switchSiteFailedUserUnlicensed();
    default:
      return Messages.switchSiteFailedNotMemberOrNotExist();
  }
}

function shouldUseInterpolatedStringLinks(errorType: RouterErrorType): boolean {
  switch (errorType) {
    case 'view-data-alert-permissions':
    case 'view-data-alert-exists':
    case 'view-data-alert-viewer':
      return true;

    default:
      return false;
  }
}

function getInterpolatedString(errorType: RouterErrorType, dataAlertErrorData: DataAlertErrorData, urls: SpacesSiteUrls): JSX.Element {
  if (errorType === 'view-data-alert-exists') {

    const replacements = { 'THE_VIEW': () => <a href={dataAlertErrorData.viewUrl}>{Messages.dataAlertErrorViewLinkText()}</a> };
    const props: DeprecatedInterpolatedString.Props = {
      baseStringKey: 'dataAlertInvalidAlertUrlError',
      replacements,
      translate: () => Messages.dataAlertInvalidAlertUrlError({THE_VIEW: '{THE_VIEW}'}),
    };

    return <DeprecatedInterpolatedString {...props} />;
  }

  if (errorType === 'view-data-alert-permissions') {
    const replacements = {
      'THE_VIEW': () => <a href={dataAlertErrorData.viewUrl}>{Messages.dataAlertErrorViewLinkText()}</a>,
      'OWNER': () => <a href={urls.userUrl(dataAlertErrorData.owner.username, dataAlertErrorData.owner.domainName)}>{dataAlertErrorData.owner.displayName}</a>,
    };
    const props: DeprecatedInterpolatedString.Props = {
      baseStringKey: 'dataAlertBadPermissionsError',
      replacements,
      translate: () => Messages.dataAlertBadPermissionsError({THE_VIEW: '{THE_VIEW}',OWNER: '{OWNER}'})
    };

    return <DeprecatedInterpolatedString {...props} />;
  }

  if (errorType === 'view-data-alert-viewer') {
    const props: DeprecatedInterpolatedString.Props = {
      baseStringKey: 'viewerCannotEditDataAlertError',
      replacements: {},
      translate: () => Messages.viewerCannotEditDataAlertError(),
    };

    return <DeprecatedInterpolatedString {...props} />;
  }

  return null;
}
